/**
 * Barra de progresso de navegação (estilo NProgress), vanilla e fora da árvore
 * React. Nasceu (ADR-024) cobrindo só a janela entre o clique do usuário e o
 * fetch/parse do chunk JS da rota seguinte (`import()` em `router.ts::handle()`).
 * Desde 28-09-2026 (ADR-046, supersede parcialmente a ADR-024) cobre também o
 * carregamento de dados de cada página, via `react/utils/page-progress.ts::
 * usePageProgress()` — e é o único indicador de carregamento de página do
 * app: o `LoadingScreen` do Minimal (spinner central por página) foi
 * descontinuado (ver `react/vendor/loading-screen/README.md`).
 *
 * O elemento (`#nav-progress-bar`) é markup+CSS estático de `index.html`,
 * mesmo padrão do `#splash-screen` — este módulo só manipula classList/estilo
 * inline dele, sem depender de `react`/`react-dom` nem de nenhum chunk já
 * carregado.
 *
 * `startNavProgress()`/`stopNavProgress()` aceitam chamadas concorrentes
 * (contador interno) — `router.ts` e `usePageProgress()` sempre chamam em
 * pares, mas uma navegação disparada antes da anterior resolver (clique
 * rápido), ou um fetch de página iniciado antes do chunk terminar de montar,
 * não pode fazer o `stop()` de uma escamotear o `start()` da outra.
 */

const BAR_ID = "nav-progress-bar";
const SPLASH_ID = "splash-screen";

/** Chunk já em cache do navegador resolve bem mais rápido que isso — não vale
 * mostrar a barra (e escondê-la de novo) só para "flashar" na tela. */
const SHOW_DELAY_MS = 150;

/** Período de graça entre o contador zerar (`stopNavProgress()`) e a barra de
 * fato começar a esconder. Existe porque o router chama `stop()` assim que o
 * chunk da rota resolve, e a página monta e chama `start()` (via
 * `usePageProgress`) para o fetch de dados poucos ms depois — sem essa
 * janela, a barra chegaria a 100%/sumiria e reiniciaria do 0% entre as duas
 * janelas de carregamento, quando o usuário deveria ver um crescimento único
 * e contínuo. */
const STOP_GRACE_MS = 100;

/** Duração da transição de largura enquanto a navegação está em andamento —
 * deliberadamente mais longa que o pior caso medido de chunk + fetch de dados
 * da página (1 a 4 s nas telas de Boletos/Empreendimentos, ver ADR-024), para
 * a barra nunca "terminar" de crescer antes do `stop()` chegar. */
const GROW_DURATION_MS = 8000;
const GROW_TARGET_PERCENT = 85;

let pendingCount = 0;
let showTimer: ReturnType<typeof setTimeout> | undefined;
let hideTimer: ReturnType<typeof setTimeout> | undefined;
let graceTimer: ReturnType<typeof setTimeout> | undefined;
let visible = false;

function getBar(): HTMLElement | null {
  return document.getElementById(BAR_ID);
}

function clearHideTimer(): void {
  if (hideTimer !== undefined) {
    clearTimeout(hideTimer);
    hideTimer = undefined;
  }
}

function clearGraceTimer(): void {
  if (graceTimer !== undefined) {
    clearTimeout(graceTimer);
    graceTimer = undefined;
  }
}

function showBar(): void {
  const bar = getBar();
  if (!bar) return;
  // Guarda do splash: a barra tem z-index 9999, acima do #splash-screen
  // estático (9998, removido só no `transitionend` por `main.ts::hideSplash`,
  // chamado depois que a 1ª rota termina de montar). Sem esta guarda, o
  // `usePageProgress()` da própria primeira página apareceria por cima do
  // splash enquanto busca seus dados. `visible` fica `false` para o `stop()`
  // correspondente ser no-op — a barra nunca chegou a ser exibida.
  if (document.getElementById(SPLASH_ID)) {
    visible = false;
    return;
  }
  clearHideTimer();
  visible = true;
  bar.style.transition = "none";
  bar.style.width = "0%";
  bar.classList.add("is-visible");
  // Força o reflow para o browser não agrupar o "width: 0%" acima com a
  // transição definida a seguir (senão nunca anima, salta direto pro alvo).
  void bar.offsetWidth;
  bar.style.transition = `width ${GROW_DURATION_MS}ms cubic-bezier(0.1, 0.6, 0.2, 1), opacity 0.2s ease`;
  bar.style.width = `${GROW_TARGET_PERCENT}%`;
}

function hideBar(): void {
  const bar = getBar();
  visible = false;
  // Defensivo: uma chamada encadeada (ex.: `stop()` extra enquanto já havia
  // um `hideBar()` pendente) não pode deixar dois `hideTimer` correndo em
  // paralelo — o mais recente sempre substitui, nunca soma.
  clearHideTimer();
  if (!bar) return;
  bar.style.transition = "width 0.25s ease, opacity 0.3s ease";
  bar.style.width = "100%";
  hideTimer = setTimeout(() => {
    bar.classList.remove("is-visible");
    hideTimer = setTimeout(() => {
      bar.style.width = "0%";
      hideTimer = undefined;
    }, 300);
  }, 200);
}

export function startNavProgress(): void {
  pendingCount++;
  if (graceTimer !== undefined) {
    // O contador tinha acabado de zerar e a barra estava em graça, prestes a
    // esconder — este `start()` chegou dentro da janela. Cancela o
    // `hideBar()` agendado e retorna sem tocar na barra: ela continua
    // visível e crescendo de onde estava, sem resetar para 0%.
    clearGraceTimer();
    return;
  }
  if (pendingCount > 1) return; // já em andamento (ou pendente de exibir)

  if (hideTimer !== undefined) {
    // A barra já passou da graça e está na fase de esconder — `hideBar()`
    // rodou, `visible` já é `false`, mas a classe/largura ainda não
    // terminaram de recolher (`hideTimer` pendente). Mostra de novo NA HORA,
    // sem o debounce de `SHOW_DELAY_MS`: ela esteve visível há poucos ms,
    // então não é o caso de "pode nunca chegar a aparecer" que o debounce
    // existe para evitar — sem este atalho, a barra ficaria parada em 100%
    // (largura já setada por `hideBar()`) pelos 150 ms do debounce antes de
    // `showBar()` finalmente resetá-la.
    clearHideTimer();
    showBar();
    return;
  }

  showTimer = setTimeout(() => {
    showTimer = undefined;
    showBar();
  }, SHOW_DELAY_MS);
}

export function stopNavProgress(): void {
  if (pendingCount > 0) pendingCount--;
  if (pendingCount > 0) return; // ainda há navegação concorrente em andamento

  if (showTimer !== undefined) {
    // Resolveu antes do debounce estourar — chunk já em cache, nunca chegou
    // a ficar visível. Só cancela o timer, sem animar nada.
    clearTimeout(showTimer);
    showTimer = undefined;
    return;
  }

  if (visible) {
    // Não esconde na hora — dá a janela de graça para um `start()` seguinte
    // (troca de rota → fetch de dados da página) assumir sem reiniciar a
    // barra. Se nada chegar dentro da janela, `hideBar()` roda normalmente.
    // `clearGraceTimer()` é defensivo: com os chamadores atuais `visible`
    // implica nenhum `graceTimer` ainda pendente, mas um `stop()` extra não
    // pode empilhar um segundo timer por trás do primeiro.
    clearGraceTimer();
    graceTimer = setTimeout(() => {
      graceTimer = undefined;
      hideBar();
    }, STOP_GRACE_MS);
  }
}
